import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, User, Building2, Mail, Phone, MapPin, Calendar,
  Package, Beaker, FileText, Clock, CheckCircle2, AlertCircle,
  Palette, Droplets, Star, Box, Truck, MessageSquare, RefreshCw,
  Info, Edit, Save, X, Loader2
} from "lucide-react";

export default function ClientRequirementDetailPage() {
  const { requirementId } = useParams();
  const navigate = useNavigate();
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit mode state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRequirementDetails = useCallback(async () => {
    setLoading(true);
    try {
      // First try the dedicated endpoint for single requirement
      const decodedId = decodeURIComponent(requirementId);
      const singleResponse = await axios.get(`${API}/zoho/client-requirement/${encodeURIComponent(decodedId)}`);
      
      if (singleResponse.data.success && singleResponse.data.requirement) {
        setRequirement(singleResponse.data.requirement);
        setLoading(false);
        return;
      }
      
      // Fallback: Fetch all requirements and find the specific one
      const response = await axios.get(`${API}/zoho/client-requirements`);
      if (response.data.success) {
        let foundRequirement = null;
        
        // Search through clients and their samples
        for (const client of response.data.clients) {
          for (const sample of client.samples) {
            // Match by sample number (requirementId is the sampleNo)
            if (sample.sampleNo === decodedId || sample.sampleNo?.toUpperCase() === decodedId.toUpperCase()) {
              foundRequirement = {
                ...sample,
                clientName: client.clientName,
                companyName: client.companyName,
                email: client.email,
                phoneNumber: client.phoneNumber,
                location: client.location
              };
              break;
            }
          }
          if (foundRequirement) break;
        }
        
        if (foundRequirement) {
          setRequirement(foundRequirement);
        } else {
          toast.error("Requirement not found");
          navigate("/client-requirements");
        }
      }
    } catch (error) {
      console.error("Error fetching requirement:", error);
      toast.error("Failed to load requirement details");
    } finally {
      setLoading(false);
    }
  }, [requirementId, navigate]);

  useEffect(() => {
    fetchRequirementDetails();
  }, [fetchRequirementDetails]);

  // Open edit dialog
  const openEditDialog = () => {
    setEditData({ ...requirement });
    setShowEditDialog(true);
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Save edited requirement
  const handleSaveEdit = async () => {
    if (!editData) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/zoho/client-requirement/${encodeURIComponent(requirement.sampleNo)}`,
        editData
      );
      
      if (response.data.success) {
        toast.success("Client Requirement updated successfully!");
        if (response.data.zohoSync?.success) {
          toast.success("✅ Synced to Zoho Creator");
        }
        setRequirement(editData);
        setShowEditDialog(false);
        setEditData(null);
      } else {
        toast.error(response.data.error || "Failed to update requirement");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update requirement");
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      High: "bg-red-100 text-red-700 border-red-200",
      Medium: "bg-amber-100 text-amber-700 border-amber-200",
      Low: "bg-green-100 text-green-700 border-green-200"
    };
    return colors[priority] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getStatusColor = (status) => {
    const colors = {
      "New": "bg-blue-100 text-blue-700",
      "Under R&D": "bg-purple-100 text-purple-700",
      "In Progress": "bg-amber-100 text-amber-700",
      "Testing": "bg-orange-100 text-orange-700",
      "Completed": "bg-green-100 text-green-700",
      "On Hold": "bg-slate-100 text-slate-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  // Helper render function for field display
  const renderDisplayField = (label, value, Icon) => (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-slate-500" />
        </div>
      )}
      <div className="flex-1">
        <p className="text-xs text-slate-500 font-poppins">{label}</p>
        <p className={`font-medium font-poppins ${value ? 'text-slate-800' : 'text-slate-400 italic'}`}>
          {value || "Not specified"}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 mx-auto text-vcc-teal animate-spin mb-4" />
          <p className="text-slate-500 font-poppins">Loading requirement details...</p>
        </div>
      </div>
    );
  }

  if (!requirement) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500 font-poppins">Requirement not found</p>
        <Button onClick={() => navigate("/client-requirements")} className="mt-4">
          Back to Requirements
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="requirement-detail-page">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Button 
          variant="ghost" 
          onClick={() => navigate("/client-requirements")}
          className="mb-4 font-poppins"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Requirements
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 font-poppins">
                {requirement.sampleNo || "Requirement Details"}
              </h1>
              <Badge className={`${getPriorityColor(requirement.priority || requirement.samplePriority)} font-poppins`}>
                {requirement.priority || requirement.samplePriority || "N/A"} Priority
              </Badge>
            </div>
            <p className="text-slate-500 font-poppins">
              {requirement.productType || "Product"} • {requirement.companyName || "Company"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openEditDialog}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins shadow-md"
              data-testid="edit-requirement-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Badge className={`${getStatusColor(requirement.jobStage)} font-poppins px-4 py-2 text-sm`}>
              <Clock className="w-4 h-4 mr-2" />
              {requirement.jobStage || "New"}
            </Badge>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Information */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="h-full">
            <CardHeader className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 font-poppins">
                <User className="w-5 h-5" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              {renderDisplayField("Client Name", requirement.clientName, User)}
              {renderDisplayField("Company Name", requirement.companyName, Building2)}
              {renderDisplayField("Email Address", requirement.email, Mail)}
              {renderDisplayField("Phone Number", requirement.phoneNumber, Phone)}
              {renderDisplayField("Location", requirement.location, MapPin)}
              {renderDisplayField("Date Submitted", requirement.dateField, Calendar)}
            </CardContent>
          </Card>
        </motion.div>

        {/* Product & Status Information */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="h-full">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 font-poppins">
                <Package className="w-5 h-5" />
                Product & Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600 font-poppins mb-1">Product Type</p>
                <p className="font-bold text-lg text-blue-800 font-poppins">
                  {requirement.productType || "Not specified"}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 font-poppins mb-1">Priority</p>
                  <Badge className={`${getPriorityColor(requirement.priority || requirement.samplePriority)} font-poppins`}>
                    {requirement.priority || requirement.samplePriority || "N/A"}
                  </Badge>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 font-poppins mb-1">Job Stage</p>
                  <Badge className={`${getStatusColor(requirement.jobStage)} font-poppins`}>
                    {requirement.jobStage || "New"}
                  </Badge>
                </div>
              </div>

              {renderDisplayField("Category", requirement.category, Beaker)}
              {renderDisplayField("Sub-Category", requirement.subCategory, Package)}
              {renderDisplayField("Zoho Record ID", requirement.zohoId, FileText)}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Product Requirements Section */}
      <motion.div
        className="mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 font-poppins">
              <Beaker className="w-5 h-5" />
              Product Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Palette className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Texture</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.texture ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.texture || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Droplets className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Color</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.color ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.color || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Purpose</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.productPurpose ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.productPurpose || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Skin/Hair Type</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.skinHairType ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.skinHairType || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Pack Size</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.packSize ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.packSize || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Quantity</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.quantity ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.quantity || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Packaging</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.packagingPreference ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.packagingPreference || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-4 h-4 text-slate-500" />
                  <p className="text-xs text-slate-500 font-poppins">Expected Delivery</p>
                </div>
                <p className={`font-medium font-poppins ${requirement.expectedDelivery ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.expectedDelivery || "Not specified"}
                </p>
              </div>
            </div>

            {/* Additional fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 font-poppins mb-2">Odour / Fragrance</p>
                <p className={`font-medium font-poppins ${requirement.odourFragrance ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.odourFragrance || "Not specified"}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 font-poppins mb-2">Ingredients Preference</p>
                <p className={`font-medium font-poppins ${requirement.ingredientsPreference ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                  {requirement.ingredientsPreference || "Not specified"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Remarks Section */}
      <motion.div
        className="mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader className="bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 font-poppins">
              <MessageSquare className="w-5 h-5" />
              Remarks & Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className={`font-poppins ${requirement.remarks ? 'text-slate-700' : 'text-slate-400 italic'}`}>
              {requirement.remarks || "No remarks provided"}
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Info Note */}
      <motion.div
        className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-poppins font-medium">Note</p>
            <p className="text-sm text-amber-700 font-poppins">
              Some fields may show &quot;Not specified&quot; if they were not filled in when the requirement was submitted in Zoho. 
              Only requirements submitted through the website form will have all fields populated.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Footer Actions */}
      <motion.div
        className="mt-6 flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-poppins text-slate-600">Priority:</span>
            <Badge className={`${getPriorityColor(requirement.priority || requirement.samplePriority)}`}>
              {requirement.priority || requirement.samplePriority || "N/A"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-poppins text-slate-600">Status:</span>
            <Badge className={`${getStatusColor(requirement.jobStage)}`}>
              {requirement.jobStage || "New"}
            </Badge>
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate("/client-requirements")}
            className="font-poppins"
          >
            Back to List
          </Button>
          <Button 
            onClick={() => navigate(`/products`)}
            className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
          >
            View in Product Development
          </Button>
        </div>
      </motion.div>

      {/* Edit Client Requirement Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Client Requirement - {editData?.sampleNo}
            </DialogTitle>
          </DialogHeader>

          {editData && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Sample No</Label>
                  <Input value={editData.sampleNo || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Client Name</Label>
                  <Input 
                    value={editData.clientName || ""} 
                    onChange={(e) => handleEditChange("clientName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Company Name</Label>
                  <Input 
                    value={editData.companyName || ""} 
                    onChange={(e) => handleEditChange("companyName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Email</Label>
                  <Input 
                    value={editData.email || ""} 
                    onChange={(e) => handleEditChange("email", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Phone Number</Label>
                  <Input 
                    value={editData.phoneNumber || ""} 
                    onChange={(e) => handleEditChange("phoneNumber", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Location</Label>
                  <Input 
                    value={editData.location || ""} 
                    onChange={(e) => handleEditChange("location", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Product Type</Label>
                  <Select 
                    value={editData.productType || ""} 
                    onValueChange={(value) => handleEditChange("productType", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Skin Care Products">Skin Care Products</SelectItem>
                      <SelectItem value="Hair Care Products">Hair Care Products</SelectItem>
                      <SelectItem value="Oral Care Products">Oral Care Products</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Priority</Label>
                  <Select 
                    value={editData.priority || editData.samplePriority || "Medium"} 
                    onValueChange={(value) => handleEditChange("priority", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Job Stage</Label>
                  <Select 
                    value={editData.jobStage || "New"} 
                    onValueChange={(value) => handleEditChange("jobStage", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Testing">Testing</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Dispatched">Dispatched</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Texture</Label>
                  <Input 
                    value={editData.texture || ""} 
                    onChange={(e) => handleEditChange("texture", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Color</Label>
                  <Input 
                    value={editData.color || ""} 
                    onChange={(e) => handleEditChange("color", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Odour/Fragrance</Label>
                  <Input 
                    value={editData.odourFragrance || ""} 
                    onChange={(e) => handleEditChange("odourFragrance", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Pack & Quantity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Packaging Preference</Label>
                  <Input 
                    value={editData.packagingPreference || ""} 
                    onChange={(e) => handleEditChange("packagingPreference", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Pack Size</Label>
                  <Input 
                    value={editData.packSize || ""} 
                    onChange={(e) => handleEditChange("packSize", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Required Quantity</Label>
                  <Input 
                    value={editData.quantity || ""} 
                    onChange={(e) => handleEditChange("quantity", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Remarks */}
              <div>
                <Label className="text-xs text-slate-600 font-poppins">Remarks</Label>
                <Textarea 
                  value={editData.remarks || ""} 
                  onChange={(e) => handleEditChange("remarks", e.target.value)}
                  className="mt-1" 
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="font-poppins"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins shadow-md"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save &amp; Sync to Zoho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
